import Image from "next/image";
import { format } from "date-fns";
import { useState } from "react";
import { useForm } from "react-hook-form";
import TagManager from "react-gtm-module";

import { getLocations } from "../lib/locations";
import { getOptions } from "../lib/options";
import { getFAQCategory } from "../lib/faq";
import { getFeaturedOptions } from "../lib/featured-options";

import Button from "../components/button";
import CollapsedQuestion from "../components/collapsed-question";
import Divider from "../components/divider";
import Hero from "../components/hero";
import Input from "../components/input";
import Select from "../components/select";
import Textarea from "../components/textarea";
import DateSelect from "../components/date-select";
import DateTimeSelect from "../components/date-time-select";
import HowToGetStarted from "../components/how-to-get-started/how-to-get-started";

import styles from "./brokers.module.scss";
import FeaturedOptionsSection from "../components/featured-options-section";
import CarouselMultiple from "../components/carousel-multiple";
import Slide from "../components/slide";
import { getGallery } from "../lib/gallery";
import config from "../config";
import SeoMeta from "../components/seo-meta";
import { getSEOPageProps } from "../lib/seo";
import { activeCampaignSubmit } from "../lib/forms";

const formatDate = (day) => {
  return format(day, "yyyy-MM-dd");
};

// Active Campaign field names.
const fieldMap = {
  companyName: "field[68]",
  bookingDate: "field[15]",
  bookingTime: "field[16]",
  location: "field[63]",
  budget: "field[112]",
  shootType: "field[29]",
  attendees: "field[64]",
  productBrand: "field[10]",
  details: "field[17]",
  subscribe: "field[24][]"
};

const budgetChoices = [
  "$0 - $5,000",
  "$5,000 - $10,000",
  "$10,000 - $25,000",
  "$25,000+"
];

export default function FilmAndPhotography({
  locations,
  questions,
  meetOurClientsGallery,
  featuredOptions,
  seo
}) {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const { register, formState, handleSubmit, watch, getValues, setValue } =
    useForm({
      mode: "onChange",
      // Default Active Campaign values for this
      // specific form.
      defaultValues: {
        u: "6",
        f: "6",
        s: "0",
        c: "0",
        m: "0",
        act: "sub",
        v: "2",
        [fieldMap.bookingDate]: ""
      }
    });

  watch();

  const onSubmit = (data) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "contact_us",
        type: "shoot",
        "type-shoot": data[fieldMap.shootType],
        "crew-size": data[fieldMap.attendees],
        budget: data[fieldMap.budget],
        location: data[fieldMap.location],
        firstname: data.firstname,
        lastname: data.lastname,
        phone: data.phone,
        email: data.email
      }
    });

    activeCampaignSubmit(data, false)
      .then(() => setSubmitted(true))
      .catch((e) => setError(e));
  };

  const onError = (e) => console.log(e);

  const { errors } = formState;

  const bookingDateField = fieldMap.bookingDate.split("[")[1].slice(0, -1);

  const getError = (field) => {
    const fieldNum = field.split("[")[1].slice(0, -1);

    if (errors.field && errors.field[fieldNum]) {
      return errors.field[fieldNum].message;
    }

    return null;
  };

  return (
    <>
      <div className="container">
        <SeoMeta {...seo} />
        <Hero
          title="Film & Photography"
          description="Book a film or photography session in design-forward, intentional spaces that can accommodate intimate or commercial shoots. Explore our available locations that have hosted leading photographers, publications and brands."
        />

        <figure className={styles.FeaturedImage}>
          <Image
            src="/images/film-photography.jpg"
            alt="Film & Photography at East Room"
            width="1240"
            height="679"
            layout="responsive"
            objectFit="cover"
          />
        </figure>
      </div>

      <div className={styles.HowToGetStartedWrapper}>
        <HowToGetStarted
          steps={[
            {
              image: { url: "/images/step-connect.jpg" },
              title: "Connect",
              description:
                "Get in touch with us by submitting the form below, providing as much detail as possible."
            },
            {
              image: { url: "/images/step-explore.jpg" },
              title: "Discover",
              description:
                "Our community manager will work with you to explore availble spaces for filming and photography."
            },
            {
              image: { url: "/images/step-host.jpg" },
              title: "Host",
              description:
                "We’ll work with you to make sure your film or photography session runs smoothly, and every detail is covered."
            }
          ]}
        />
      </div>

      <FeaturedOptionsSection options={featuredOptions} />

      <div className="container">
        <div className={styles.BrokerFormWrapper}>
          <div className={styles.BrokerFormContent}>
            <h2 className="font-bold">
              Book your next film or photography session
            </h2>

            <p>
              Submit the form and one of our workspace experts will reach out to
              you with additional details.
            </p>
          </div>

          {submitted && (
            <p className={styles.ConfirmationMessage}>
              Thank you, we will be in touch shortly!
            </p>
          )}

          {!submitted && (
            <form
              className={styles.BrokerForm}
              onSubmit={handleSubmit(onSubmit, onError)}
            >
              <div className="form-group-inline">
                <Input
                  type="text"
                  placeholder="First name *"
                  {...register("firstname", {
                    required: "First name is required"
                  })}
                  errorText={errors.firstname && errors.firstname.message}
                  inline
                />
                <Input
                  type="text"
                  placeholder="Last name *"
                  {...register("lastname", {
                    required: "Last name is required"
                  })}
                  errorText={errors.lastname && errors.lastname.message}
                  inline
                />
              </div>

              <Input
                type="text"
                placeholder="Work email address *"
                {...register("email", { required: "Email is required" })}
                errorText={errors.email && errors.email.message}
              />

              <Input
                type="text"
                placeholder="Phone number *"
                {...register("phone", {
                  required: "Phone number is required"
                })}
                errorText={errors.phone && errors.phone.message}
              />
              <Input
                type="text"
                placeholder="Company name *"
                {...register(fieldMap.companyName, {
                  required: "Company name is required"
                })}
                errorText={getError(fieldMap.companyName)}
              />

              <div className="form-group-inline">
                <DateSelect
                  {...register(fieldMap.bookingDate, {
                    required: "Booking date is required"
                  })}
                  placeholder="Desired Event Date *"
                  selectedDate={getValues().field?.[bookingDateField]}
                  disabledDates={(day) => day < new Date()}
                  onChange={(day) => {
                    setValue(fieldMap.bookingDate, formatDate(day));
                  }}
                  errorText={getError(fieldMap.bookingDate)}
                  inline
                />

                <DateTimeSelect
                  {...register(fieldMap.bookingTime, {
                    required: "Booking time is required"
                  })}
                  date={getValues().field?.[bookingDateField]}
                  type="text"
                  placeholder="Desired Booking Time *"
                  onChange={(e) => {
                    setValue(fieldMap.bookingTime, e.target.value);
                  }}
                  errorText={getError(fieldMap.bookingTime)}
                  inline
                />
              </div>

              <Select
                {...register(fieldMap.location, {
                  required: "Location is required"
                })}
                errorText={getError(fieldMap.location)}
              >
                <option value="" disabled selected>
                  Select your location of interest *
                </option>
                {locations.map((location) => (
                  <option
                    value={`${location.address1}, ${location.city} ${location.province}`}
                    key={location.slug}
                  >
                    {location.address1}, {location.city} {location.province}
                  </option>
                ))}
              </Select>

              <Select
                {...register(fieldMap.budget, {
                  required: "Shoot Budget is required"
                })}
                errorText={getError(fieldMap.budget)}
              >
                <option value="" disabled selected>
                  Shoot Budget *
                </option>
                {budgetChoices.map((choice) => (
                  <option value={choice} key={choice}>
                    {choice}
                  </option>
                ))}
              </Select>

              <Select
                {...register(fieldMap.shootType, {
                  required: "Type of shoot is required"
                })}
                errorText={getError(fieldMap.shootType)}
              >
                <option value="" disabled selected>
                  Select the type of shoot *
                </option>
                <option value="Photoshoot">Photoshoot</option>
                <option value="Video Shoot">Video Shoot</option>
              </Select>

              <Input
                type="text"
                placeholder="Crew size *"
                {...register(fieldMap.attendees, {
                  required: "Crew size is required"
                })}
                errorText={getError(fieldMap.attendees)}
              />

              <Input
                type="text"
                placeholder="Product/Brand being featured *"
                {...register(fieldMap.productBrand, {
                  required: "Product or brand is required"
                })}
                errorText={getError(fieldMap.productBrand)}
              />

              <Textarea
                type="text"
                placeholder="Shoot description and any additional information"
                rows="3"
                {...register(fieldMap.details)}
              />

              {error && <p>{error}</p>}

              <div className={styles.BrokerFormButtonWrapper}>
                <Button rounded outline small type="submit">
                  Get in Touch
                </Button>
              </div>
            </form>
          )}
        </div>

        <div className={styles.CarouselWrapper}>
          <CarouselMultiple
            title={meetOurClientsGallery.title}
            description={meetOurClientsGallery.description}
          >
            {meetOurClientsGallery.galleryItemsCollection.items.map((slide) => (
              <Slide
                key={slide.title}
                image={`${slide.image.url}?w=400&fm=webp&q=80`}
                title={slide.title}
                byline={slide.subtitle}
              />
            ))}
          </CarouselMultiple>
        </div>

        <Divider />

        <section className={styles.BrokerQuestionsSection}>
          <div className={styles.BrokerQuestionHeader}>
            <h3>Have a question?</h3>
          </div>
          <div className={styles.BrokerQuestionContent}>
            {questions.map((question) => (
              <CollapsedQuestion
                key={question.title}
                question={question.title}
                answer={question.description}
              />
            ))}
          </div>
        </section>
      </div>
    </>
  );
}

export async function getStaticProps() {
  try {
    const [locations, options, questions, meetOurClientsGallery, featuredOptions, seo] =
      await Promise.all([
        getLocations(),
        getOptions(),
        getFAQCategory("film-and-photography"),
        getGallery("meet-our-clients"),
        getFeaturedOptions("featured-spaces"),
        getSEOPageProps({ resolvedUrl: "/film-and-photography" })
      ]);

    return {
      props: {
        locations,
        options,
        questions,
        meetOurClientsGallery,
        featuredOptions,
        seo
      },
      revalidate: config.ISR_TIMING
    };
  } catch (e) {
    console.log(e.message);
  }
}
